import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import ModalContainer from '../../../uiToolkit/ModalContainer';
import TextInput from '../../../uiToolkit/Inputs/TextInput';
import Dropdown from '../../../uiToolkit/Inputs/Dropdown';
import Button from '../../../uiToolkit/Buttons/Button';
import LoadingSpinner from '../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import ProjectsStore from '../store/ProjectsStore';
import {PROJECT_NAME_MAX_LENGTH} from '../../../data/models/Projects/Project';
import {projectsText} from '../projectsText';
import styles from './MoveToProjectModal.module.css';

const NO_PROJECT = 'no_project';

interface Props {
  courseId: string;
  currentProjectId?: string | null;
  onClose: () => void;
  onMoved: () => void;
  projectsStore?: ProjectsStore;
}

const MoveToProjectModal = inject('projectsStore')(
  observer(
    ({courseId, currentProjectId, onClose, onMoved, projectsStore}: Props) => {
      const {organizationId} = useParams();
      const [selected, setSelected] = useState(currentProjectId || NO_PROJECT);
      const [newName, setNewName] = useState('');
      const [loaded, setLoaded] = useState(false);
      const [saving, setSaving] = useState(false);

      useEffect(() => {
        if (!organizationId) return;
        projectsStore!
          .fetchProjects(organizationId)
          .then(() => setLoaded(true));
      }, [organizationId]);

      const onMove = async () => {
        if (!organizationId || saving) return;
        setSaving(true);
        let projectId = selected === NO_PROJECT ? null : selected;
        if (newName.trim()) {
          const createdId = await projectsStore!.createProject(
            organizationId,
            newName.trim(),
          );
          if (!createdId) {
            setSaving(false);
            return;
          }
          projectId = createdId;
        }
        const success = await projectsStore!.assignCourses(
          organizationId,
          projectId,
          [courseId],
        );
        setSaving(false);
        if (!success) {
          projectsStore!.fetchProjects(organizationId);
          return;
        }
        const project = projectsStore!.projects.find(
          item => item.id === projectId,
        );
        toast.success(
          project
            ? projectsText.movedToProject(project.name)
            : projectsText.removedFromProject,
          toastConfig,
        );
        onMoved();
      };

      const options = [
        {key: NO_PROJECT, value: projectsText.noProject},
        ...projectsStore!.projects.map(project => ({
          key: project.id,
          value: project.name,
        })),
      ];

      return (
        <ModalContainer onClose={onClose} className={styles.modal}>
          <div className={styles.content}>
            <h3 className={styles.title}>{projectsText.moveTitle}</h3>
            {loaded ? (
              <Dropdown
                heading={projectsText.project}
                options={options}
                selectedOption={selected}
                setSelectedOption={setSelected}
                disabled={!!newName.trim()}
                enableSearch
              />
            ) : (
              <div className={styles.loading}>
                <LoadingSpinner size={32} />
              </div>
            )}
            <TextInput
              heading={projectsText.newProjectInline}
              placeholder={projectsText.projectNamePlaceholder}
              value={newName}
              onChange={value => {
                setNewName(value);
                setSelected(NO_PROJECT);
              }}
              onClear={() => setNewName('')}
              maxCharacters={PROJECT_NAME_MAX_LENGTH}
            />
            <div className={styles.actions}>
              <Button
                title={projectsText.cancel}
                uiType="text"
                onClick={onClose}
              />
              <Button
                title={projectsText.move}
                disabled={
                  !loaded ||
                  (!newName.trim() &&
                    selected === (currentProjectId || NO_PROJECT))
                }
                isLoading={saving}
                onClick={onMove}
              />
            </div>
          </div>
        </ModalContainer>
      );
    },
  ),
);

export default MoveToProjectModal;
